import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { validateDateAfter } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_HOMEPAGE } from '@/modules/main/router';
import { newsService } from '@/services/news';
import { helpers, required, requiredIf } from '@vuelidate/validators';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useMixins() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const model = reactive({
        id: StringUtils.toNumber(route.params.id), // cloneId
        forAppId: null,
        name: '',
        targetType: -1,
        target: '',
        startTime: null,
        endTime: null,
        publishTime: null,
        unpublishTime: null,
        fromRegisterTime: null,
        toRegisterTime: null,
        image: null,
        imageFile: null,
        thumbnail: null,
        thumbnailFile: null
    });

    const updateContentValidation = {
        name: { required },
        targetType: { required },
        imageFile: {},
        thumbnailFile: {},
        target: {
            requiredIf: requiredIf(() => model.targetType >= 0),
            urlIf: helpers.withMessage(
                'The value is not a valid URL address',
                (value: string, vm: any) =>
                    vm.targetType != 0 ||
                    /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi.test(
                        value
                    )
            )
        },
        toRegisterTime: {
            requiredIf: requiredIf(() => model.fromRegisterTime),
            validateDateAfter: validateDateAfter('fromRegisterTime')
        }
    };

    const validations = {
        ...updateContentValidation,
        publishTime: { required },
        startTime: { validateDateAfter: validateDateAfter() },
        endTime: { requiredIf: requiredIf(() => model.startTime), validateDateAfter: validateDateAfter('startTime') },
        unpublishTime: { required, validateDateAfter: validateDateAfter('endTime') }
    };

    const fetch = crudAction.executeAction({
        canExecute: () => model.id != null,
        request: () => newsService.get(model.id),
        onSuccess: ({ data }) => {
            model.id = data.id;
            model.name = data.name;
            model.targetType = data.targetType;
            model.target = data.target;
            model.image = data.image;
            model.thumbnail = data.thumbnail;
            model.startTime = data?.startTime ? new Date(data.startTime) : null;
            model.endTime = data?.endTime ? new Date(data.endTime) : null;
            model.publishTime = new Date(data.publishTime);
            model.unpublishTime = new Date(data.unpublishTime);
            model.fromRegisterTime = data?.fromRegisterTime ? new Date(data.fromRegisterTime) : null;
            model.toRegisterTime = data?.toRegisterTime ? new Date(data.toRegisterTime) : null;

            if (data.target) {
                if (data.target.startsWith('link:')) {
                    model.target = data.target.replace(/link:/, '');
                    model.targetType = 0;
                } else if (data.target.startsWith('popup:')) {
                    model.target = data.target.replace(/popup:/, '');
                    model.targetType = 1;
                } else if (data.target.startsWith('deeplink:')) {
                    model.target = data.target.replace(/deeplink:/, '');
                    model.targetType = 2;
                } else {
                    model.targetType = -1;
                }
            } else {
                model.targetType = -1;
            }
        },
        onError: () => router.push({ name: ROUTE_NAME_HOMEPAGE })
    });

    return { model, validations, updateContentValidation, NewsOptions, fetch };
}

export const NewsOptions = {
    Type: [
        { label: 'None', value: -1, background: 'badge-info' },
        { label: 'Links', value: 0, background: 'badge-success' },
        { label: 'Popup', value: 1, background: 'badge-primary' },
        { label: 'Deep', value: 2, background: 'badge-warning' }
    ],
    Target: [
        { label: 'DailyBonus', value: 'DailyBonus' },
        { label: 'Shop', value: 'Shop' },
        { label: 'StartTotem', value: 'StartTotem' },
        { label: 'Tutorial', value: 'Tutorial' },
        { label: 'ChangePassword', value: 'ChangePassword' },
        { label: 'NewVersion', value: 'NewVersion' },
        { label: 'Rating', value: 'Rating' },
        { label: 'AvatarShop', value: 'AvatarShop' },
        { label: 'Arena', value: 'Arena' },
        { label: 'Messenger', value: 'Messenger' },
        { label: 'Inbox', value: 'Inbox' },
        { label: 'League', value: 'League' },
        { label: 'Quest', value: 'Quest' },
        { label: 'Minigames', value: 'Minigames' },
        { label: 'Challenge', value: 'Challenge' }
    ],
    Status: [
        { label: 'None', value: 0, background: 'info' },
        { label: 'Enabled', value: 1, background: 'success' },
        { label: 'Published', value: 2, background: 'warning' },
        { label: 'Started', value: 3, background: 'info' },
        { label: 'Ended', value: 4, background: 'success' },
        { label: 'Unpublished', value: 5, background: 'warning' },
        { label: 'Disabled', value: 6, background: 'info' }
    ]
};
